import * as THREE from 'three'
import React, { Suspense, useEffect, useRef, useState, useMemo } from 'react'
import { Canvas, useFrame } from 'react-three-fiber'
import { Reflector, useTexture, OrbitControls, MeshWobbleMaterial } from '@react-three/drei'
import { Physics, useBox, useSphere, useConvexPolyhedron } from "@react-three/cannon";
import useStore from './store'
import * as Tone from "tone"
import { TweenMax, Sine, Linear, gsap, Slowmo } from "gsap"

import inst from "./inst"

Math.lerp = function (value1, value2, amount) {
  amount = amount < 0 ? 0 : amount;
  amount = amount > 1 ? 1 : amount;
  return (1 - amount) * value1 + amount * value2;
};

const vec = new THREE.Vector3()
const backCol = new THREE.Color('#ffffff')
const sphereCol = new THREE.Color('#000')
const hitSphereCol = new THREE.Color('#000')
const hitBoxCol = new THREE.Color('#000')
const topSphereCol = new THREE.Color('#000')
const topSphereHoverColor = new THREE.Color('#000')
const boxCol = new THREE.Color('#888')

export default function App() {
  const mut = useStore((state) => state.mut)
  
  return (
    <>
    <Canvas  concurrent pixelRatio={[1, 2]} camera={{ position: [0, -50, 25], fov: 70 }}>
      <color attach="background" args={[backCol]} />
      <fog attach="fog" args={[backCol, 50, 80]} />
      <Suspense fallback={null}>
        <HiddenPlane />
        <ambientLight intensity={1} />
        <directionalLight position={[-10, 10, 5]} intensity={1} />
        <directionalLight position={[0, -10, 0]} intensity={0.75} />
        <group position-y={-0.25}>
          <Physics
            defaultContactMaterial={{
              'contactEquationStiffness': 1e5,
              'restitution': 0.8,
            }}
            gravity={[0, -15, 0]}
          >
            <Spheres />
            <Boxes />
          </Physics>
        </group>
          <Ground />
        <Intro />
      </Suspense>
    </Canvas>
    </>
  )
}

function Graph(props) {
  const { synth } = useStore((state) => state.audio)
  const ref = useRef()
  useFrame(() => {
    for (let i = 0; i < 64; i++) {
      obj.position.set(i * 0.04, synth.data[i] / 1000, 0)
      obj.updateMatrix()
      ref.current.setMatrixAt(i, obj.matrix)
    }
    ref.current.instanceMatrix.needsUpdate = true
  })
  return (
    <instancedMesh ref={ref} args={[null, null, 64]} {...props}>
      <planeGeometry args={[0.02, 0.05]} />
      <meshBasicMaterial toneMapped={false} transparent opacity={0.25} />
    </instancedMesh>
  )
}


function Boxes() {
  let notes = ["C", "D", 'E', 'F', 'G', 'A']
  const amt = 18;
  const rot = 3
  const range = 30
  return (
    <>
      {new Array(amt).fill().map((v, i) => {
        return <Box
        i={i}
        note={notes[i%notes.length] + (2 + Math.floor(i/notes.length))}
          rotation={[
            0,
            0,
            i % 2 == 0 ? Math.PI / rot : -Math.PI / rot
          ]}
          position={[
            i % 2 == 0 ? (-range + i) : (range - i),
            28 - (Math.floor(i / 2) * 6),
            0
          ]}
          size={3 + (Math.floor(i / 2)*2)} key={i} />
      })}
    </>
  )
}
function Spheres() {
  let notes = ["C", "D"]
  
  const amt = useStore(state => state.amt)
  return (
    <>
      {new Array(amt).fill().map((v, i) => {
        return <Sphere dur={4} 
        note={"C3"}
        key={i} size={0.65} i={i} />
      })}
    </>
  )
}

function Box(props) {
  const t = 0.1
  const tweens = useRef([]);
  const material = useRef();
  const mut = useStore(state => state.mut)
  const set = useStore(state => state.set)
  const [ref] = useBox(() => ({ args: [props.size * t, props.size, props.size], onCollide: play, ...props }))
  const synth = useMemo(() => inst.create(props.i), [])
  useFrame(state => {
    
  })
  let anim = () => {
    ref.current.material.color.copy(hitBoxCol)
    tweens.current[1] && tweens.current[1].kill()
    tweens.current[1] = TweenMax.to(ref.current.material.color, 4, {
      r: boxCol.r,
      g: boxCol.g,
      b: boxCol.b,
      ease: "slow(0.7, 0.7, false)"
    });
   
    material.current.factor = Math.random()*2
    tweens.current[2] && tweens.current[2].kill()
    tweens.current[2] = TweenMax.to(material.current, 2, {
      factor: 0,
    });
  }
  let play = () => {
    synth.triggerAttackRelease(props.note, 20, '+0.05', 0.15 + (props.i * 0.03));
    anim()
  }
  return (
    <mesh ref={ref} 
    onPointerOver={() => {
      set('clicked', true)
      document.body.style.cursor = 'pointer';
      play()
    }}
    onPointerOut={() => {
      document.body.style.cursor = 'unset';
    }}
    onPointerDown={() => {
      set('clicked', true)
      play()
    }}
    >
      <boxBufferGeometry args={[props.size * t, props.size, props.size * t,32,32,32]} />
      <MeshWobbleMaterial attach="material" color={boxCol} ref={material} speed={4} factor={0} />
    </mesh>
  )
}

function Sphere(props) {
  const top = useRef();
  const beat = useRef(0);
  const tweens = useRef([]);
  //const synth = useMemo(() => inst.create(props.i), [])
  const mut = useStore(state => state.mut)
  const loopDur = useStore(state => state.loopDur)
  const clicked = useStore(state => state.clicked)
  useEffect(() => {
    clicked && start()
  }, [clicked])
  let start = (e) => {
    TweenMax.to(top.current.scale, 2, {
      x: 1,
      y: 1,
      z: 1
    });
  }
  let anim = () => {
    ref.current.material.color.copy(hitSphereCol)
    tweens.current[0] && tweens.current[0].kill()
    tweens.current[0] = TweenMax.to(ref.current.material.color, 1, {
      r: sphereCol.r,
      g: sphereCol.g,
      b: sphereCol.b
    });
  }
  let scaleUp = () => {
    tweens.current[1] && tweens.current[1].kill()
    tweens.current[1] = TweenMax.to(top.current.scale, 1, {
      x: 2,
      y: 2,
      z: 2
    });
  }
  let scaleDown = () => {
    tweens.current[2] && tweens.current[2].kill()
    tweens.current[2] = TweenMax. to(top.current.scale, 1, {
      x: 1,
      y: 1,
      z: 1
    });
  }

  let play = (e) => {
    if (!e) {
      //synth.triggerAttackRelease("F#3", "16n", '+0.05', 0.9);
    } else {
      //synth.triggerAttackRelease( 120 + e.contact.impactVelocity * 0.5, "16n", '+0.05', e.contact.impactVelocity * 0.01);
      anim(e)
    }
  }
  let spawn = () => {
    tweens.current[3] && tweens.current[3].kill()
    tweens.current[3] = TweenMax.to(top.current.scale, 0.1, {
      x: top.current.scale.x * 0.6,
      y: top.current.scale.y * 0.6,
      z: top.current.scale.z * 0.6
    });
    tweens.current[4] && tweens.current[4].kill()
    tweens.current[4] = TweenMax.to(top.current.scale, 1, {
      x: top.current.scale.x,
      y: top.current.scale.y,
      z: top.current.scale.z,
      delay: 0.1
    });
    api.position.set(...mut.spheres[props.i])
    api.angularVelocity.set(0, 0, 0)
    api.velocity.set(0, -25, 0)
  }
  let fetch = () => { 
    api.velocity.set(0, 0, 0)
    api.angularVelocity.set(0, 0, 0)
    tweens.current[3] && tweens.current[3].kill()
    tweens.current[3] = TweenMax.to(ref.current.scale, 0.2, {
      x: 0,
      y: 0,
      z: 0,
      onComplete: () => {
        spawn()
        ref.current.scale.set(1,1,1)
      }
    });
  }

  const [ref, api] = useSphere(() => ({ mass: props.size * props.size * props.size, args: props.size, onCollide: play, position: [
      mut.spheres[props.i][0],
      mut.spheres[props.i][1] - 500,
      mut.spheres[props.i][2]
    ] 
  }))
  useFrame(state => {
    const t = state.clock.getElapsedTime()
    if(!mut.startTime && clicked){
      mut.startTime = t
    }
    top.current.position.x = mut.spheres[props.i][0];
    top.current.position.y = mut.spheres[props.i][1];
    top.current.position.z = mut.spheres[props.i][2];
    if (Math.floor((t - mut.startTime) / loopDur) !== beat.current && clicked) {
      if (ref.current.position.y < -30) {
        spawn() 
      }
      beat.current = Math.floor((t - mut.startTime) / loopDur);
    }
  })
  return (
    <group>
      <mesh ref={top}
        onPointerDown={() => {
          mut.downSphere = props.i
          fetch()
        }}
        onPointerUp={() => {
          mut.downSphere = null
        }}
        onPointerOver={() => {
          scaleUp()
          top.current.material.color.copy(topSphereHoverColor)
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          scaleDown()
          top.current.material.color.copy(topSphereCol)
          document.body.style.cursor = 'unset';
        }}
        scale={[0,0,0]}
      >
        <meshStandardMaterial fog={false} toneMapped={false} color={topSphereCol} />
        <sphereBufferGeometry args={[props.size * 2, 32, 32]} />
      </mesh>
      <mesh ref={ref} >
        <meshStandardMaterial  fog={false} toneMapped={false} color={sphereCol} />
        <sphereBufferGeometry args={[props.size, 32, 32]} />
      </mesh>
    </group>
  )
}



function HiddenPlane() {
  const startY = useStore(state => state.startY)
  const mut = useStore(state => state.mut)
  let move = (e) => {
    if (mut.downSphere !== null) {
      mut.spheres[mut.downSphere][0] = e.point.x
    }
    if (mut.downBox !== null) {
      mut.spheres[mut.downBox][0] = e.point.x
    }
  }
  return (
    <mesh
      onPointerUp={() => {
        mut.downSphere = null
      }}
      onPointerMove={move} visible={false} rotation={[Math.PI / 2, 0, 0]} position={[0, startY, 0]}>
      <planeBufferGeometry args={[200, 200]} />
      <meshStandardMaterial />
    </mesh>
  )
}

function Ground() {
  return (
    <Reflector
      resolution={1024}
      mirror={0.2}
      blur={[500, 100]}
      mixBlur={0.1}
      mixStrength={0.5}
      depthScale={0}
      minDepthThreshold={0.1}
      maxDepthThreshold={1}
      position={[0, 0, -2]}
      scale={[2, 2, 1]}
      args={[150, 150]}>
      {(Material, props) => <Material metalness={0.15} color={backCol} roughness={1} {...props} />}
    </Reflector>
  )
}


function Intro() {
  const clicked = useStore((state) => state.clicked)
  // Zoom in camera when user has pressed start
  return useFrame((state) => {
    if (clicked) {
      state.camera.position.lerp(vec.set(0, -45, 25), 0.02)
      state.camera.lookAt(0, 0, 0)
    }
  })
}
